/**
 * Calendar dates as ISO strings (`YYYY-MM-DD`), for every module that plans in days: release
 * dates, project ranges, events. A date has no time zone — a launch on the 19th is on the 19th
 * wherever it's read — so nothing here goes near the reader's clock; "today" comes from the
 * business clock (./business-time).
 *
 * Arithmetic counts whole days through UTC midnights, only because UTC has no DST: a local day
 * can be 23 or 25 hours long. ISO strings also compare and sort correctly as plain strings.
 *
 * Pure and isomorphic, with no React: schemas and servers import it as
 * `@noissue/os-ui/lib/iso-date`.
 */

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;
export const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const DAY_MS = 86_400_000;
const PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Year, month (1–12) and day of an ISO date. */
export function dateParts(isoDate: string): { year: number; month: number; day: number } {
  const [year, month, day] = isoDate.split('-').map(Number);
  return { year: year!, month: month!, day: day! };
}

const iso = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/** A real calendar date: `2026-02-30` has the shape but is not a day. */
export function isIsoDate(value: string): boolean {
  if (!PATTERN.test(value)) return false;
  const { year, month, day } = dateParts(value);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/** Whole days since 1970-01-01. */
export function dayNumber(isoDate: string): number {
  const { year, month, day } = dateParts(isoDate);
  return Math.round(Date.UTC(year, month - 1, day) / DAY_MS);
}

export const isoDateOfDay = (day: number): string =>
  new Date(day * DAY_MS).toISOString().slice(0, 10);

export const addDays = (isoDate: string, days: number): string =>
  isoDateOfDay(dayNumber(isoDate) + days);

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export const daysBetween = (from: string, to: string): number => dayNumber(to) - dayNumber(from);

/** The first of a month; `month` may run past 1–12 and rolls into the next or previous year. */
export function firstOfMonth(year: number, month: number): string {
  const date = new Date(Date.UTC(year, month - 1, 1));
  return iso(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
}

/** 0 = Sunday … 6 = Saturday. */
export const weekdayOf = (isoDate: string): number =>
  new Date(dayNumber(isoDate) * DAY_MS).getUTCDay();

/** The Monday on or before a date. */
export const mondayOf = (isoDate: string): string =>
  addDays(isoDate, -((weekdayOf(isoDate) + 6) % 7));

/** The ISO-8601 week number (weeks start Monday; week 1 holds the year's first Thursday). */
export function isoWeek(isoDate: string): number {
  const thursday = addDays(isoDate, 3 - ((weekdayOf(isoDate) + 6) % 7));
  return Math.floor(daysBetween(`${thursday.slice(0, 4)}-01-01`, thursday) / 7) + 1;
}

/** The first day of the quarter a date falls in, quarters counted from `fiscalStartMonth`. */
export function quarterStart(isoDate: string, fiscalStartMonth = 1): string {
  const { year, month } = dateParts(isoDate);
  const offset = (month - fiscalStartMonth + 12) % 12;
  return firstOfMonth(year, month - (offset % 3));
}

/** A span of days, both ends inclusive. */
export interface DateWindow {
  start: string;
  end: string;
}

/**
 * Whole quarters around `today`: from the start of the quarter `before` quarters back to the
 * end of the quarter `after` quarters ahead. A planning chart that rolls with the quarter.
 */
export function rollingWindow(
  today: string,
  {
    before,
    after,
    fiscalStartMonth = 1,
  }: { before: number; after: number; fiscalStartMonth?: number },
): DateWindow {
  const { year, month } = dateParts(quarterStart(today, fiscalStartMonth));
  return {
    start: firstOfMonth(year, month - 3 * before),
    end: addDays(firstOfMonth(year, month + 3 * (after + 1)), -1),
  };
}
